import React, { useEffect, useState, useCallback } from "react";
import { Box, Button, Container, FormControl, Grid2, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography, Alert } from "@mui/material";
import { icon } from "@/app/style/icons";
import { useMutation } from "@apollo/client";
import { REGISTER_WITH_OTP } from "@auth/graphql/mutations/signupWithOTP";
import { LocalStorage } from "@/types/enums";
import { setAuth } from "@store/slices/auth";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification, showSuccessNotification } from "@/utils/errorHandling";
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";
import { UPDATE_USER_AND_COMPANY_INFO } from "@company/graphql/mutations/updateUserAndCompanyInfo";
import { useNavigate } from "react-router-dom";
import ConsentSection, { ConsentState } from "@auth/components/ConsentSection";
import { validateConsent, isConsentValid } from "@/utils/validateConsent";
import AVVCreation, { AVVState } from "@company/components/AVVCreation";
import { GERMAN_STATES } from "@/app/constants/germanStates";
import { availableViewportHeight } from "@core/layout/AppLayout";
import { RouteTo } from "@routing/RouteMap";
import { AuthPayload } from "@/app/graphql/codegen";
import { mapAuthError } from "@/utils/authErrorMapping";
import { useOnboardingPersistence } from "@auth/hooks/useOnboardingPersistence";
import { OnboardingDraft } from "@/types/onboarding";

const OnboardingPage = () => {
    const dispatch = useDispatch();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();

    /* ---------------- Persistent draft integration ---------------- */
    // placeholder default draft
    const [currentStep, setCurrentStep] = useState<1 | 2>(1);

    // Form data
    const [name, setName] = useState("");
    const [title, setTitle] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [bundesland, setBundesland] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    
    // State tracking
    const [hasRegistered, setHasRegistered] = useState(isAuthenticated);
    const [avvCompleted, setAVVCompleted] = useState(false);
    
    // Consent management
    const [consents, setConsents] = useState<ConsentState>({
        agbAccepted: false,
        datenschutzAccepted: false
    });
    const [consentErrors, setConsentErrors] = useState<{agb?: string; datenschutz?: string}>({});
    
    // Form errors (only relevant for registration portion)
    const [formErrors, setFormErrors] = useState<{ email?: string; password?: string; submit?: string }>({});
    
    // AVV state
    const [avvState, setAVVState] = useState<AVVState>({ status: 'not_created' });

    // ----------------- persistence helpers -----------------
    const buildSnapshot = useCallback((): OnboardingDraft => ({
        currentStep,
        email,
        consents,
        hasRegistered,
        name,
        title,
        companyName,
        bundesland,
        avvState,
        avvCompleted,
        savedAt: new Date().toISOString(),
    }), [currentStep, email, consents, hasRegistered, name, title, companyName, bundesland, avvState, avvCompleted]);
    
    const hydrateDraft = (draft: OnboardingDraft) => {
        if (draft.currentStep) setCurrentStep(draft.currentStep);
        if (draft.email) setEmail(draft.email);
        if (draft.consents) setConsents(draft.consents);
        if (draft.hasRegistered !== undefined) setHasRegistered(draft.hasRegistered);
        if (draft.name) setName(draft.name);
        if (draft.title) setTitle(draft.title);
        if (draft.companyName) setCompanyName(draft.companyName);
        if (draft.bundesland) setBundesland(draft.bundesland);
        if (draft.avvState) setAVVState(draft.avvState);
        if (draft.avvCompleted !== undefined) setAVVCompleted(draft.avvCompleted);
    };
    
    const { isRestoring, clearDraft } = useOnboardingPersistence(buildSnapshot, hydrateDraft);

    const [registerMutation, { data }] = useMutation(REGISTER_WITH_OTP);
    const [updateInfos, { data: updateInfosData, loading: updateInfosLoading }] = useMutation(UPDATE_USER_AND_COMPANY_INFO);

    useEffect(() => {
        if (data?.signup) {
            const authPayload: AuthPayload = data.signup;

            dispatch(setAuth(authPayload));

            // Synchronize with localStorage.
            localStorage.setItem(LocalStorage.AUTH_TOKEN, authPayload.authToken);
            localStorage.setItem(LocalStorage.USER_ID, authPayload.id);
            localStorage.setItem(LocalStorage.USER_EMAIL, authPayload.email);

            showSuccessNotification("Sie wurden erfolgreich registriert", dispatch, "Erfolg");

            setHasRegistered(true);
            // Stay on step 1 to show company details form
            console.log(data);
        }
    }, [data]);

    useEffect(() => {
        if (updateInfosData) {
            clearDraft();
            navigate(RouteTo.SHOP);
        }
    }, [updateInfosData]);

    const handleRegister = () => {
        // Validate consents before registering
        const errors = validateConsent(consents);
        setConsentErrors(errors);
        
        if (!isConsentValid(consents)) {
            return;
        }

        registerMutation({
            variables: { email, password, name: "placeholder" },
            onError: (error) => {
                const errMap = mapAuthError(error);
                setFormErrors((prev) => ({ ...prev, ...errMap }));

                handleErrorWithNotification(
                    error,
                    dispatch,
                    "Registrierung fehlgeschlagen",
                    errMap.submit || errMap.password || "Registrierung fehlgeschlagen. Bitte prüfen Sie Ihre Eingaben."
                );
            },
        });
    };

    const handleStep1Continue = () => {
        // Validation for company details before moving to AVV step
        if (!hasRegistered || !name || !title || !companyName || !bundesland) {
            return;
        }
        setCurrentStep(2);
    };

    const handleUpdateInfos = () => {
        updateInfos({
            variables: { 
                id: localStorage.getItem(LocalStorage.USER_ID), 
                name, 
                title, 
                companyName,
                bundesland 
            },
        });
    };

    const handleConsentChange = (newConsents: ConsentState) => {
        setConsents(newConsents);
        // Clear errors when user makes changes
        setConsentErrors({});
    };

    const handleAVVStateChange = (newState: AVVState) => {
        setAVVState(newState);
        // Mark AVV as completed when it's created and ready for download
        if (newState.status === 'created' && newState.downloadUrl) {
            setAVVCompleted(true);
        }
    };

    const renderStep1 = () => (
        <Grid2 container size={12} spacing={1.5}>
            {!hasRegistered && (
                <>
                    <Grid2 size={12}>
                        <TextField 
                            fullWidth 
                            placeholder="E-Mail" 
                            type="email" 
                            value={email} 
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (formErrors.email) setFormErrors((prev) => ({ ...prev, email: undefined }));
                            }} 
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <TextField
                            fullWidth
                            placeholder="Passwort"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (formErrors.password) setFormErrors((prev) => ({ ...prev, password: undefined }));
                            }}
                            error={!!formErrors.password}
                            helperText={formErrors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <icon.hidePassword sx={{ fontSize: 20 }} /> : <icon.showPassword sx={{ fontSize: 20 }} />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid2>
                    
                    {/* Existing account prompt */}
                    <Grid2 size={12} sx={{ textAlign: 'center' }}>
                        <Typography variant="body2">
                            Du hast bereits ein Konto?{" "}
                            <Button
                                variant="text"
                                onClick={() => navigate(RouteTo.LOGIN)}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                                Hier&nbsp;Anmelden
                            </Button>
                        </Typography>
                    </Grid2>

                    <Grid2 px={1.5}>
                        <ConsentSection
                            consents={consents}
                            onConsentChange={handleConsentChange}
                            errors={consentErrors}
                        />
                    </Grid2>

                    <Grid2 size={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained" 
                            onClick={handleRegister} 
                            disabled={!email || !password || !isConsentValid(consents)}
                        >
                            Registrieren
                        </Button>
                    </Grid2>
                </>
            )}

            {hasRegistered && (
                <>
                    <Grid2 size={12}>
                        <TextField 
                            fullWidth 
                            placeholder="Name" 
                            type="text" 
                            value={name} 
                            onChange={(e) => setName(e.target.value)} 
                            required
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <TextField 
                            fullWidth 
                            placeholder="Position" 
                            type="text" 
                            value={title} 
                            onChange={(e) => setTitle(e.target.value)} 
                            required
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <TextField 
                            fullWidth 
                            placeholder="Name des Unternehmens" 
                            type="text" 
                            value={companyName} 
                            onChange={(e) => setCompanyName(e.target.value)} 
                            required
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <FormControl fullWidth required>
                            <InputLabel>Bundesland</InputLabel>
                            <Select
                                value={bundesland}
                                label="Bundesland"
                                onChange={(e) => setBundesland(e.target.value)}
                            >
                                {GERMAN_STATES.map((state: string) => (
                                    <MenuItem key={state} value={state}>
                                        {state}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid2>

                    <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained" 
                            onClick={handleStep1Continue} 
                            disabled={!hasRegistered || !name || !title || !companyName || !bundesland}
                        >
                            Weiter zu AVV
                        </Button>
                    </Grid2>
                </>
            )}
        </Grid2>
    );

    const renderStep2 = () => (
        <Grid2 container size={12} spacing={3}>
            <Grid2 size={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Letzter Schritt:</strong> Bitte erstellen und laden Sie Ihren Auftragsverarbeitungsvertrag (AVV) herunter. 
                        Dieser ist für die Nutzung unserer Dienste erforderlich.
                    </Typography>
                </Alert>
            </Grid2>

            <Grid2 size={12}>
                <Box sx={{ 
                    p: 3,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200'
                }}>
                    <Typography 
                        variant="h6" 
                        color="text.primary" 
                        sx={{ mb: 2 }}
                    >
                        Unternehmensdaten für AVV:
                    </Typography>
                    
                    <Grid2 container spacing={2}>
                        <Grid2 size={6}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Kundenname:</strong> {companyName}
                            </Typography>
                        </Grid2>
                        <Grid2 size={6}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Weisungsbefugter:</strong> {name}
                            </Typography>
                        </Grid2>
                        <Grid2 size={6}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Bundesland:</strong> {bundesland}
                            </Typography>
                        </Grid2>
                    </Grid2>
                </Box>
            </Grid2>

            <Grid2 size={12}>
                <AVVCreation
                    companyData={{
                        name: companyName,
                        ownerName: name,
                        address: bundesland
                    }}
                    initialState={avvState}
                    onStateChange={handleAVVStateChange}
                    disabled={updateInfosLoading}
                    inlineMode={true}
                    autoGenerate={true}
                />
            </Grid2>

            <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button 
                    variant="outlined" 
                    onClick={() => setCurrentStep(1)}
                    disabled={updateInfosLoading}
                >
                    Zurück
                </Button>
                
                <Button 
                    variant="contained" 
                    onClick={handleUpdateInfos} 
                    disabled={!avvCompleted || updateInfosLoading}
                >
                    {updateInfosLoading ? 'Wird abgeschlossen...' : 'Onboarding abschließen'}
                </Button>
            </Grid2>
        </Grid2>
    );

    // Get container max width based on current step
    const getContainerMaxWidth = () => {
        return currentStep === 2 ? "md" : "xs";
    };

    if (isRestoring) {
        // Zeige einen simplen Ladezustand, um Flackern zu vermeiden, während der Entwurf geladen wird
        return (
            <Box sx={{ height: availableViewportHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>Lade gespeicherten Entwurf…</Typography>
            </Box>
        );
    }

    return (
        <Box 
            sx={{ 
                height: availableViewportHeight,
                overflow: "auto",
            }}
        >
            <Container 
                maxWidth={getContainerMaxWidth()} 
                sx={{ py: 6 }}
            >
                <Grid2 container size={12} spacing={3} justifyContent={"center"}>
                    <Grid2 container spacing={1} justifyContent={"center"} sx={{ textAlign: 'center', mb: 2 }}>
                        <Grid2 size={12}>
                            <Typography variant={"h3"}>Onboarding</Typography>
                        </Grid2>

                        <Grid2 size={12}>
                            {currentStep === 1 ? (
                                hasRegistered ? (
                                    <Typography>Gib ein paar Daten ein.</Typography>
                                ) : (
                                    <Typography>Lass uns nun die Registrierung starten.</Typography>
                                )
                            ) : (
                                <Typography>Erstellen Sie Ihren Auftragsverarbeitungsvertrag.</Typography>
                            )}
                        </Grid2>
                    </Grid2>

                    {currentStep === 1 ? renderStep1() : renderStep2()}
                </Grid2>
            </Container>
        </Box>
    );
};

export default OnboardingPage;
